import React, { useState, useEffect, useRef, useCallback } from "react"
import { FormattedMessage, injectIntl, defineMessages } from "react-intl"
import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import { isError } from "../../../state/helpers/Error"
import Icon from "../../../common/icon/icon.component.jsx"
import DeprecatedUserSelect from "../../../common/user/selection.component.jsx"
import ActivityFeedEventComponent from "./ActivityFeedEventComponent"
import LoadingSpinner from "../../../common/spinner/loading.component"
import SearchService from "../../../state/search/SearchService"
import moment from "moment"
import ActivityFeedDateLine from "../ActivityFeedDateLine"
import ActivityFeedNoResult from "../ActivityFeedNoResult"
import SideBar from "../../../common/layout/sidebar.component.jsx"
import ActivityFeedEventSearchResult from "../../../state/search/Dto"
import { ActivityFeedEventSearchResultDto } from "../../../state/search/Dto"
import { Intl } from "../../../state/helpers/IntlProps"
import AccountVerificationComponent from "../../../auth/components/AccountVerificationComponent"
import { Redirect } from "react-router"
import ShortcutsContainer from "../../../shortcuts/sections/ShortcutsContainer"

type Props = {
    intl: Intl
}

const messages = defineMessages({
    activity_feed_title: {
        id: "dashboard.activity-feed.title",
        defaultMessage: "Activity Feed",
    },
    all_users: {
        id: "dashboard.activity-feed.filter.user.all_placeholder",
        defaultMessage: "All users",
    },
    end_feed: {
        id: "dashboard.activity-feed.end_feed",
        defaultMessage: "End of activity feed. Nothing more to show.",
    },
    none_for_user: {
        id: "dashboard.activity-feed.none_for_user",
        defaultMessage: "No activities for the selected user(s)."
    }
})

const ActivityFeedComponent = (props: Props) => {

    const [isLoaded, setIsLoaded] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [activities, setActivities] = useState<ActivityFeedEventSearchResult[]>([])
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
    const [pageNumber, setPageNumber] = useState(1)
    const [onBoardingStatus, setOnBoardingStatus] = useState<string>(AppContext.getAccount().attributes.onBoardingStatus)
    const [verified, setVerified] = useState<boolean>(AppContext.getAccount().attributes.verifiedAt != null)

    SideBar.activateMenu("dashboard")
    if (onBoardingStatus === "pending") {
        AppContext.getApiClient().get("/accounts/account-details").then(res => {
            setOnBoardingStatus(res.attributes.onBoardingStatus)
        })
    }

    useEffect(() => {
        const userId = AppContext.getUserLoggedIn().id
        AppContext.getApiClient().get("/users/" + userId + "/preferences").then(res => {
            const dashboardActivityFeedUserIds = res.attributes.dashboardActivityFeedUserIds
            if (dashboardActivityFeedUserIds.length) {
                setSelectedUserIds(dashboardActivityFeedUserIds)
            }
            loadActivities()
        })
    }, [])

    const loadActivities = (() => {

        let searchFilters = {}
        if (selectedUserIds.length) {
            searchFilters = {
                createdByUserId: selectedUserIds
            }
        }

        SearchService.listingSearch({
            keywords: "*",
            objects: ["activity-feed-events"],
            filters: searchFilters,
            pageSize: 10,
            activePage: pageNumber,
            sortByField: "createdAt",
            sortAsc: false,
            lightResult: true,
        }).then(res => {
            if (!isError(res)) {
                const newActivities = activities.concat(res.getData().map(item => {
                    const unknownItem = item as unknown
                    return new ActivityFeedEventSearchResult(unknownItem as ActivityFeedEventSearchResultDto)
                }))
                setActivities(newActivities)
                setHasMore(res.metadata.totalItemsCount > newActivities.length)
                setIsLoaded(true)
            }
        })
    })

    useEffect(() => {
        setIsLoaded(false)
        loadActivities()
    }, [selectedUserIds])

    const observer = useRef<IntersectionObserver>()
    const lastActivityRef = useCallback(elem => {

        if (!isLoaded) return
        if (observer.current) {
            observer.current.disconnect()
        }
        observer.current = new IntersectionObserver(entries => {

            if (entries[0].isIntersecting && hasMore) {
                setPageNumber(pageNumber => pageNumber + 1)
            }

        })
        if (elem) {
            observer.current.observe(elem)
        }
    }, [isLoaded, hasMore])

    useEffect(() => {
        if (pageNumber > 1) {
            loadActivities()
        }
    }, [pageNumber])

    const displayActivities = (() => {

        const activitiesByDate = {}
        activities.map((activity: ActivityFeedEventSearchResult) => {

            const date = moment(activity.getCreatedAt()).startOf("day").format()
            if (!activitiesByDate.hasOwnProperty(date)) {
                activitiesByDate[date] = [activity]
            } else {
                activitiesByDate[date] = activitiesByDate[date].concat([activity])
            }
        })

        const sortedActivitesByDate = Object.entries(activitiesByDate).sort().reverse()
        let count = 0

        return (
            sortedActivitesByDate.map((byDate: object, i) => {
                return (
                    <div key={i}>
                        <ActivityFeedDateLine date={byDate[0]} />
                        <div className="activity-feed">
                            {
                                byDate[1].map((activity, index) => {
                                    count++
                                    if (index == byDate[1].length - 1 || index == activities.length - 1) {
                                        return (
                                            <div key={index} ref={count === activities.length ? lastActivityRef : undefined}>
                                                <div className="last-item">
                                                    <ActivityFeedEventComponent activity={activity} />
                                                </div>
                                            </div>
                                        )
                                    } else {
                                        return (
                                            <div key={index} ref={count === activities.length ? lastActivityRef : undefined}>
                                                <ActivityFeedEventComponent activity={activity} />
                                            </div>
                                        )
                                    }
                                })
                            }
                        </div>
                    </div>
                )
            })
        )

    })
    if (onBoardingStatus === "pending" && !verified) {
        return <AccountVerificationComponent onAccountVerified={(verified) => {
            setVerified(verified)
        }} />
    }
    if (onBoardingStatus === "pending" && verified) {
        return <Redirect to={"/welcome"} />
    }
    return (
        <>
            <div>
                <ShortcutsContainer />
            </div><div>
                <div className="ml-10 mr-10">
                    <div className="d-flex justify-content-between">
                        <div>
                            <h3 className={"pl-3 mb-0 custom-lead text-secondary"}>
                                <FormattedMessage {...messages.activity_feed_title} />
                            </h3>
                        </div>

                        <div className="d-flex pr-3">
                            <div className="my-auto pr-1">
                                <Icon value={"user"} />
                            </div>

                            <div className={"dropdown"}>
                                <DeprecatedUserSelect
                                    multi={true}
                                    placeholder={props.intl.formatMessage(messages.all_users)}
                                    style={{
                                        width: "200px",
                                    }}
                                    value={selectedUserIds}
                                    onChange={selectedUserId => {
                                        setActivities([])
                                        setSelectedUserIds(selectedUserId as Array<string>)
                                        setPageNumber(1)
                                        const id = AppContext.getUserLoggedIn().id
                                        AppContext.getApiClient().patch("/users/" + id + "/preferences", {
                                            dashboardActivityFeedUserIds: selectedUserId
                                        })
                                    }} />
                            </div>
                        </div>
                    </div>

                    {isLoaded ?
                        <div className={"row justify-content-center"}>
                            <div className="col-12">

                                {displayActivities()}

                                {!hasMore && activities.length !== 0 ?
                                    <div className="d-flex mb-5 justify-content-center">
                                        <FormattedMessage {...messages.end_feed} />
                                    </div>
                                    :
                                    selectedUserIds.length !== 0 && <div className="d-flex mb-5 justify-content-center">
                                        <FormattedMessage {...messages.none_for_user} />
                                    </div>}

                                {activities.length <= 0 && selectedUserIds.length === 0 ?
                                    <ActivityFeedNoResult /> : null}

                            </div>

                        </div> : <LoadingSpinner />}
                </div>
            </div></>
    )
}

export default injectIntl(ActivityFeedComponent)



// WEBPACK FOOTER //
// ./src/dashboard/activity-feed/components/ActivityFeedComponent.tsx